<template>
    <footer class="tablet-footer" :class="isDark ? 'tablet-footer--dark' : 'tablet-footer--light'">

        <!-- ─ Bloco principal ──────────────────────────────────────── -->
        <div class="tablet-footer__inner">

            <!-- Endereço -->
            <div v-if="temEndereco" class="footer-block">
                <div class="footer-block__icon-wrap">
                    <v-icon :color="primaryColor" size="22">mdi-map-marker-outline</v-icon>
                </div>
                <div class="footer-block__content">
                    <span class="footer-block__title">Onde estamos</span>
                    <span class="footer-block__text">
                        {{ configuracoes.rua }}, {{ configuracoes.numero }}
                        <template v-if="configuracoes.bairro && configuracoes.bairro != '0'">
                            — {{ configuracoes.bairro }}
                        </template>
                    </span>
                    <span class="footer-block__text footer-block__text--muted">
                        {{ configuracoes.municipio }} / {{ configuracoes.uf }}
                    </span>
                </div>
            </div>

            <!-- Divisor vertical -->
            <div v-if="temEndereco && temHorarios" class="footer-divider" />

            <!-- Horários de funcionamento -->
            <div v-if="temHorarios" class="footer-block footer-block--horarios">
                <div class="footer-block__icon-wrap">
                    <v-icon :color="primaryColor" size="22">mdi-clock-outline</v-icon>
                </div>
                <div class="footer-block__content">
                    <span class="footer-block__title">{{ $t('tablet.horariosFuncionamento') }}</span>
                    <div class="horarios-grid">
                        <template v-for="dia in configuracoes.horarios">
                            <span
                                :key="'d-' + dia.isoWeekday"
                                class="horarios-dia"
                                :class="{ 'horarios-dia--hoje': isHoje(dia.isoWeekday) }"
                            >
                                {{ tSistema('horario.dias.' + dia.isoWeekday, dia.diaSemana) }}
                            </span>
                            <span
                                :key="'h-' + dia.isoWeekday"
                                class="horarios-valor"
                                :class="{
                                    'horarios-dia--hoje': isHoje(dia.isoWeekday),
                                    'horarios-valor--fechado': !dia.aberto
                                }"
                            >
                                <template v-if="!dia.aberto">
                                    {{ $t('tablet.fechado') }}
                                </template>
                                <template v-else>
                                    {{ dia.horario }}
                                    <template v-if="dia.segundoHorarioAtivo">
                                        &nbsp;|&nbsp;{{ dia.segundoHorario }}
                                    </template>
                                </template>
                            </span>
                        </template>
                    </div>
                </div>
            </div>

        </div>

        <!-- ─ Rodapé inferior ─────────────────────────────────────── -->
        <div class="tablet-footer__bottom">
            <div class="footer-copy">
                <v-icon size="14" class="mr-1" :color="isDark ? '#888' : '#aaa'">mdi-copyright</v-icon>
                {{ currentYear }} &nbsp;
                <strong>{{ configuracoes.fantasia }}</strong>
                <span v-if="configuracoes.slogan" class="footer-slogan">
                    &nbsp;·&nbsp; {{ configuracoes.slogan }}
                </span>
            </div>
        </div>

        <!-- ─ Propaganda ZaperMenu ────────────────────────────────── -->
        <div v-if="configuracoes.permitir_anuncios" class="tablet-footer__ad">
            <div class="footer-ad-inner">
                <div class="footer-ad-badge">ZaperMenu</div>
                <span class="footer-ad-text">
                    {{ $t('tablet.anuncioZaper') }}
                </span>
                <span class="footer-ad-contact">
                    <v-icon size="13" class="mr-1">mdi-whatsapp</v-icon>
                    (49) 9 8895-1210
                </span>
            </div>
        </div>

    </footer>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'FooterTablet',

    computed: {
        ...mapState(['configuracoes']),

        isDark() {
            return this.$vuetify.theme.isDark;
        },

        primaryColor() {
            return this.$theme?.primary || this.$vuetify.theme.themes.light.primary;
        },

        currentYear() {
            return new Date().getFullYear();
        },

        currentIsoWeekday() {
            // 1 = segunda, 7 = domingo (ISO)
            return new Date().getDay() === 0 ? 7 : new Date().getDay();
        },

        temEndereco() {
            return !!(this.configuracoes.rua || this.configuracoes.municipio);
        },

        temHorarios() {
            return Array.isArray(this.configuracoes.horarios) && this.configuracoes.horarios.length > 0;
        },
    },

    methods: {
        isHoje(isoWeekday) {
            return this.currentIsoWeekday === isoWeekday;
        },
    },
};
</script>

<style scoped>
/* ── Base ──────────────────────────────────────────────────────── */

.tablet-footer {
    margin-top: 48px;
    border-top: 1px solid transparent;
    font-size: 13px;
}

.tablet-footer--light {
    background: #f8f8f8;
    border-top-color: #e8e8e8;
    color: #444;
}

.tablet-footer--dark {
    background: #151515;
    border-top-color: #2a2a2a;
    color: #ccc;
}

/* ── Bloco principal (endereço + horários) ─────────────────────── */

.tablet-footer__inner {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: wrap;
    gap: 32px;
    padding: 36px 48px 28px;
}

/* Divisor vertical entre os blocos */
.footer-divider {
    width: 1px;
    min-height: 80px;
    align-self: stretch;
    background: rgba(128, 128, 128, 0.2);
    flex-shrink: 0;
}

/* Bloco genérico (ícone + conteúdo) */
.footer-block {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex: 1;
    min-width: 220px;
    max-width: 340px;
}

.footer-block--horarios {
    max-width: 480px;
}

.footer-block__icon-wrap {
    margin-top: 3px;
    flex-shrink: 0;
}

.footer-block__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.footer-block__title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.1px;
    opacity: 0.5;
    margin-bottom: 4px;
}

.footer-block__text {
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.5;
}

.footer-block__text--muted {
    font-size: 12px;
    opacity: 0.6;
}

/* ── Grid de horários ─────────────────────────────────────────── */

.horarios-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 3px 16px;
    margin-top: 2px;
}

.horarios-dia {
    font-size: 12.5px;
    font-weight: 500;
    opacity: 0.75;
    white-space: nowrap;
}

.horarios-valor {
    font-size: 12.5px;
    font-weight: 400;
}

.horarios-valor--fechado {
    opacity: 0.4;
    font-style: italic;
}

/* Destaque do dia de hoje */
.horarios-dia--hoje {
    font-weight: 700 !important;
    opacity: 1 !important;
    color: var(--v-primary-base);
}

/* ── Rodapé copyright ─────────────────────────────────────────── */

.tablet-footer__bottom {
    display: flex;
    justify-content: center;
    padding: 10px 48px 14px;
    border-top: 1px solid rgba(128, 128, 128, 0.12);
}

.footer-copy {
    display: flex;
    align-items: center;
    font-size: 12px;
    opacity: 0.55;
}

.footer-slogan {
    font-style: italic;
}

/* ── Rodapé propaganda ZaperMenu ──────────────────────────────── */

.tablet-footer__ad {
    display: flex;
    justify-content: center;
    padding: 12px 48px 20px;
}

.footer-ad-inner {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
}

.footer-ad-badge {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--v-primary-base);
    border: 1.5px solid var(--v-primary-base);
    border-radius: 6px;
    padding: 2px 8px;
    opacity: 0.7;
    flex-shrink: 0;
}

.footer-ad-text {
    font-size: 11.5px;
    opacity: 0.4;
}

.footer-ad-contact {
    font-size: 11.5px;
    opacity: 0.45;
    display: flex;
    align-items: center;
    white-space: nowrap;
}
</style>
